<template>
  <div id="customersList">
    <card :title="$t('a-customers:customers')" no-footer>
      <div slot="card-header-right" class="d-flex">
        <input id="filterName" v-model="filter" :placeholder="$t('a-customers:filter') | capitalize" type="text" class="form-control form-control-sm mr-2">
      </div>

      <div v-if="filteredCustomers.length" id="customersCardBody" slot="card-body" class="d-flex">
        <div v-for="customer in filteredCustomers"
             :id="'customer' + customer.id"
             :key="customer.id"
             :data-customer-name="customer.name"
             :data-customer-id="customer.id"
        >
          <div v-observe-visibility="{
                 callback: (isVisible, entry) => getCustomerGroups(isVisible, entry, customer),
                 throttle: 300,
               }"
               class="customer-card border rounded p-3 text-center mr-3"
          >
            <avatar :name="customer.name" :picture-url="customer.logo" class="mx-auto" size="xl" />
            <h4 :title="customer.name" class="my-3">{{ customer.name }}</h4>

            <small class="id font-italic text-muted">Id: {{ customer.id }}</small>

            <div class="buttons">
              <span :id="'adminCustomersList-editCustomer-' + customer.id"
                    class="icon-edit"
                    @click="editCustomer(customer)"
              />
              <span v-if="$store.getters.authenticatedUserCustomer.id !== customer.id"
                    :id="'adminCustomersList-deleteCustomer-' + customer.id"
                    class="icon-trash"
                    @click="confirmDeleteCustomer(customer)"
              />
            </div>

            <div class="billing">
              <span v-if="customer.billing" :title="$t('a-customers:billingEnabled') | capitalize" class="icon-billing" />
            </div>
          </div>

          <div v-b-toggle="'customer' + customer.id + 'Fleets'" class="pointer d-flex justify-content-between align-items-center border rounded py-2 px-3 mt-1 mr-3">
            {{ $t('a-customers:fleets') | capitalize }}
            <span :id="'customer' + customer.id + 'FleetsCount'" class="badge badge-primary">{{ customer.fleets.totalCount }}</span>
          </div>
          <b-collapse :id="'customer' + customer.id + 'Fleets'" class="customer-group-list mr-3">
            <div v-for="fleet in customer.fleets.list"
                 :id="'customer' + customer.id + '-fleet' + fleet.id"
                 :key="fleet.id"
                 :data-fleet-id="fleet.id"
                 class="d-flex justify-content-between align-items-center p-2 pointer"
                 @click="goToFleet(fleet.id)"
            >
              <span :title="fleet.name">
                <i class="icon-fleet no-pointer mr-2" />
                {{ fleet.name }}
              </span>
            </div>
          </b-collapse>

          <div v-b-toggle="'customer' + customer.id + 'Clusters'" class="pointer d-flex justify-content-between align-items-center border rounded py-2 px-3 mt-1 mr-3">
            {{ $t('a-customers:clusters') | capitalize }}
            <span :id="'customer' + customer.id + 'ClustersCount'" class="badge badge-primary">{{ customer.clusters.totalCount }}</span>
          </div>
          <b-collapse :id="'customer' + customer.id + 'Clusters'" class="customer-group-list mr-3">
            <div v-for="cluster in customer.clusters.list"
                 :id="'customer' + customer.id + '-cluster' + cluster.id"
                 :key="cluster.id"
                 :data-cluster-id="cluster.id"
                 class="d-flex justify-content-between align-items-center p-2 pointer"
                 @click="goToCluster(cluster.id)"
            >
              <span :title="cluster.name">
                <i class="icon-cluster no-pointer mr-2" />
                {{ cluster.name }}
              </span>
            </div>
          </b-collapse>
        </div>
      </div>

      <div v-else slot="card-body" class="text-center text-muted font-italic">{{ $t('a-customers:noCustomers') | capitalize }}</div>
    </card>

    <confirm id="deleteCustomerConfirm"
             v-model="showDeleteCustomer"
             :message="$t('a-customers:confirmDelete', {customer: customerToDelete.name}) | capitalize"
             @confirm:validate="deleteCustomer"
    />

    <floating-button :click-handler="() => { showCustomerForm=true }" icon-classes="icon-add" />
    <customers-form v-model="showCustomerForm" :customer-to-edit="customerToEdit" @success="getCustomers" @cancel="customerToEdit={}" />
  </div>
</template>

<script>
import { filter } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

import CustomersForm from './CustomersForm'
import BCollapse from 'bootstrap-vue/es/components/collapse/collapse'
import bToggle from 'bootstrap-vue/es/directives/toggle/toggle'
import { ObserveVisibility } from 'vue-observe-visibility'

export default {
  titleKey: 'administrationCustomers',

  components: { CustomersForm, BCollapse },

  directives: {
    'b-toggle': bToggle,
    'observe-visibility': ObserveVisibility
  },

  data () {
    return {
      filter: '',
      // Add / edit customer
      showCustomerForm: false,
      customerToEdit: {},
      // Delete customer
      showDeleteCustomer: false,
      customerToDelete: {}
    }
  },

  computed: {
    // Filter customers
    filteredCustomers () {
      return filter(this.$store.getters.customers, customer => customer.name.toLowerCase().indexOf(this.filter.toLowerCase()) > -1)
    }
  },

  created () {
    this.getCustomers()
  },

  methods: {
    getCustomers () {
      return this.$store.dispatch('getCustomers')
    },

    // Delay getCustomerGroups only when customer visible
    getCustomerGroups (visibility, ioEntry, customer) {
      if (visibility) {
        if (customer.fleets.totalCount === '-') this.$store.dispatch('getCustomerAllFleets', customer)
        if (customer.clusters.totalCount === '-') this.$store.dispatch('getCustomerAllClusters', customer)
      }
    },

    goToFleet (fleetId) {
      // Save id as the selected one
      this.$store.dispatch('saveSelectedFleetId', fleetId)
      // Go to overview page
      this.$router.push('/fleets')
    },

    goToCluster (clusterId) {
      // Save id as the selected one
      this.$store.dispatch('saveSelectedClusterId', clusterId)
      // Go to overview page
      this.$router.push('/clusters')
    },

    editCustomer (customer) {
      this.customerToEdit = customer
      this.showCustomerForm = true
    },

    confirmDeleteCustomer (customer) {
      this.customerToDelete = customer
      this.showDeleteCustomer = true
    },

    async deleteCustomer () {
      let error
      [error] = await to(this.$api.deleteCustomer(this.customerToDelete))
      if (error) return

      eventBus.$emit('message-success', this.$t('a-customers:removeSuccess', { customer: this.customerToDelete.name }))
      // Update selection
      this.$store.commit('removeCustomerInGatewaysSelection', { customer: this.customerToDelete })
      this.$store.commit('removeCustomerInEndDevicesSelection', { customer: this.customerToDelete })
      // Refresh customers
      this.getCustomers()
      // Reset
      this.customerToDelete = {}
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  #customersCardBody {
    padding: $card-spacer-x;
    margin: -$card-spacer-x;
    overflow-x: auto;

    .customer-card {
      position: relative;
      min-width: 250px;
      max-width: 300px;
      transition: all 0.35s ease;

      h4 {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .id {
        position: absolute;
        top: 10px;
        left: 10px;
      }

      .buttons {
        position: absolute;
        top: 5px;
        right: 5px;

        span {
          opacity: 0.5;
          transition: all 0.35s ease;

          &:hover {
            opacity: 0.9;
          }
        }
      }

      .billing {
        position: absolute;
        right: 5px;
        bottom: 5px;
        opacity: 0.3;

        span {
          cursor: default;
        }
      }
    }

    .customer-group-list {
      // 252px is the height of 7 items without scroll
      max-height: 252px;
      overflow-y: auto;

      &.collapsing {
        overflow-y: hidden;
      }

      span {
        display: inline-block;
        width: 228px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
</style>
